import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { BillingFeature } from 'webapp/enums/enums';
import { TourId } from 'webapp/enums/tour';
import { isAlive, toSorted } from 'webapp/utils/common';
import { initialMetricSort, sortByConnectionStatus } from 'webapp/utils/metric-sort';
import { isNumber } from 'webapp/utils/number';

import definite from 'webapp/helpers/definite';
import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import onWindowEvents from 'webapp/modifiers/on-window-events';
import willDestroy from 'webapp/modifiers/will-destroy';
import CommonListAutoLoadedComponent from 'webapp/components/common/list/auto-loaded';
import CommonRequestTrialButtonComponent from 'webapp/components/common/request-trial-button';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import CommonUpgradePlanButtonComponent from 'webapp/components/common/upgrade-plan-button';
import DesignerLeftPanelMetricsMetricComponent from 'webapp/components/designer/left-panel/metrics/metric';
import type { Args as BaseArgs } from 'webapp/components/designer/left-panel/section-inner-base';
import DesignerLeftPanelSectionInnerBaseComponent from 'webapp/components/designer/left-panel/section-inner-base';
import FilterLineComponent from 'webapp/components/filter/line';
import FilterNoResultsComponent from 'webapp/components/filter/no-results';
import SubmitButtonComponent from 'webapp/components/submit-button';

import ActiveFeatures from 'webapp/models-custom/active-features';
import FilterSortMetricInDesigner from 'webapp/models-custom/filter-sort/metric-in-designer';
import { MetricFilterType, SourceCategory } from 'webapp/models-custom/filter-sort/metric-select';

import type { TrackingOriginTypeForTrialPopupOpened } from 'webapp/services/billing';
import type { onConnectSourceResult } from 'webapp/services/connect';

interface Args extends BaseArgs {
	preselectedSource?: string; // can be generic key or specific id
}

export default class DesignerLeftPanelMetricsComponent extends DesignerLeftPanelSectionInnerBaseComponent<Args> {
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/custom-queries')
	declare mmCustomQueriesService: Service.ModelManager.CustomQueries;
	@service('model-manager/scripts') declare mmScriptsService: Service.ModelManager.Scripts;
	@service('connect') declare connectService: Service.Connect;
	@service('billing') declare billingService: Service.Billing;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	availableTourId = TourId.DESIGNER__DRAG_N_DROP;

	filterSort: FilterSortMetricInDesigner;
	trackingOrigin = 'designer_source_limitation' as TrackingOriginTypeForTrialPopupOpened;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.filterSort = new FilterSortMetricInDesigner({
			noResultsMiddleText: 'in our Metric library ',
			getAllItems: () => {
				return this.metrics;
			},
			additionalSorts: [sortByConnectionStatus],
		});

		if (this.args.preselectedSource) {
			this.setSelectedSource(this.args.preselectedSource);
		}
	}

	@cached
	get unsortedMetrics() {
		return this.mmMetricsService.metrics.filter((metric) => !metric.isDeprecated);
	}

	get metrics() {
		const metrics = this.unsortedMetrics;
		const sortedMetrics = toSorted(metrics, initialMetricSort);

		return sortedMetrics;
	}

	get metricTypeFilter() {
		return this.filterSort.findFilter(1);
	}

	get sourcesFilter() {
		return this.filterSort.findFilter(2);
	}

	get isConnectedSourceSelected() {
		return !!this.source;
	}

	get selectedSourceId() {
		const selected = this.sourcesFilter?.selected;
		return selected?.length === 1 ? (selected[0] as string) : undefined;
	}

	get sourceId() {
		const selectedSourceId = this.selectedSourceId;
		if (!selectedSourceId) {
			return '';
		}
		const isCategory = (Object.values(SourceCategory) as string[]).includes(selectedSourceId);
		return isCategory ? '' : selectedSourceId;
	}

	@cached
	get source() {
		// a specific source is chosen
		const sourceId = this.sourceId;
		if (sourceId && isNumber(sourceId)) {
			return this.mmSourcesService.findModel(sourceId);
		}
		return null;
	}

	@cached
	get sourceType() {
		if (this.source) {
			return this.source.sourceType;
		} else if (this.sourceId) {
			return this.mmSourceTypesService.findModelByKey(this.sourceId);
		} else {
			return null;
		}
	}

	get sourceName() {
		return this.source?.titleAndSubtitle || this.sourceType?.name || '';
	}

	get isSourceQbOnly() {
		return !!this.sourceType?.isQbOnly;
	}

	// ------ single source selected: upgrading -----

	get limitationForSelectedSource() {
		const sourceType = this.sourceType;
		if (sourceType) {
			if (sourceType.metricBuilderIntegration) {
				return BillingFeature.ADVANCED_DATA_SOURCES;
			} else if (sourceType.isWorkflowIntegration) {
				return BillingFeature.WORKFLOW_INTEGRATIONS;
			} else if (sourceType.isPremium) {
				return BillingFeature.QB;
			} else if (sourceType.isToken) {
				return BillingFeature.DATA_PUSH;
			}
		}
		return undefined;
	}

	get limitationName() {
		const limitation = this.limitationForSelectedSource;
		return limitation ? ActiveFeatures.featureInfos[limitation]?.name || '' : '';
	}

	get canRequestTrial() {
		return this.billingService.canRequestTrial;
	}

	get showCannotUseWarning() {
		if (this.sourceType?.metricBuilderIntegration) {
			return !this.billingService.canCreateCustomIntegration;
		}
		const limitation = this.limitationForSelectedSource;
		return !!limitation && !this.billingService.checkIfCan(limitation);
	}

	// ------ results / no results logic -----

	@cached
	get resultMetrics() {
		return this.filterSort.results;
	}

	get isSearchActive() {
		return this.filterSort.isSearchActive;
	}

	get isQueryFilterSelected() {
		return (
			this.metricTypeFilter?.selected.includes(MetricFilterType.QUERY) &&
			this.metricTypeFilter?.selected.length === 1
		);
	}

	get isScriptsFilterSelected() {
		return (
			this.metricTypeFilter?.selected.includes(MetricFilterType.SCRIPT) &&
			this.metricTypeFilter?.selected.length === 1
		);
	}

	@cached
	get selectedSourceHasNoMetrics() {
		const sourceId = this.sourceId;
		return (
			sourceId &&
			!this.metrics.some((m) => {
				return m.isFromSourceListingValue(sourceId);
			})
		);
	}

	get showNextStepsSection() {
		// only if we have one source (connected or generic) selected
		return !!this.sourceId;
	}

	get isEmptyVisualizationFlow() {
		return this.selectedSourceHasNoMetrics && !this.isSourceQbOnly;
	}

	get showConnectInfo() {
		return !this.isConnectedSourceSelected;
	}

	get showQbInfo() {
		return (
			this.sourceType?.isQBSupported &&
			(this.isConnectedSourceSelected || this.isSourceQbOnly) &&
			(this.isSearchActive || this.isQueryFilterSelected)
		);
	}

	get showScriptInfo() {
		return (
			this.isConnectedSourceSelected && !this.isSearchActive && this.isScriptsFilterSelected
		);
	}

	get showMetricRequestInfo() {
		return (
			!this.isQueryFilterSelected &&
			!this.isScriptsFilterSelected &&
			!this.sourceType?.isQbOnly &&
			!this.sourceType?.isWithCustomToken
		);
	}

	// ------ Actions ------

	@action
	contactUs() {
		this.currentUserService.chatWithSupport();
	}

	@action
	openMetricRequest() {
		this.mmMetricsService.openMetricRequestForm();
	}

	@action
	openGSheetsVideo() {
		this.navigationService.openGoogleSheetsVideoPopup('designer');
	}

	@action
	connectSource(sourceTypeKey: string) {
		if (!sourceTypeKey) {
			return;
		}
		void this.connectService
			.connectSourceFromIdOrKey(sourceTypeKey, { origin: 'metric-popup' })
			.then((result: onConnectSourceResult) => {
				if (isAlive(this) && result.isSuccess) {
					this.setSelectedSource(String(result.source.id));
				}
			});
	}

	@action
	createQuery() {
		const sourceId = this.sourceId ? +this.sourceId : undefined;
		this.mmCustomQueriesService.openCreateNewPopup('designer', undefined, sourceId);
	}

	@action
	createScript() {
		this.mmScriptsService.openCreateNewPopup('designer');
	}

	@action
	setSelectedSource(sourceId: string) {
		const sourcesFilter = this.sourcesFilter;
		if (sourcesFilter) {
			sourcesFilter.selectOption(sourceId);
		}
	}

	// ----- Draggable -----

	@action
	setupMetricList(node: HTMLElement) {
		this.listNode = node;
		// this.refreshDraggable(); // will be called as result of auto-loaded-list::onVisibleItemsChanged
		node?.addEventListener('resize', this.checkIfMetricListIsFilled); // called from left panel when collapsing visualizations section
	}

	@action
	teardownMetricList() {
		this.cleanupDraggable();
		this.listNode?.removeEventListener('resize', this.checkIfMetricListIsFilled);
		this.listNode = null;
	}

	@action
	onListVisibleItemsChanged() {
		this.refreshDraggable();
		if (this.showCustomLink) {
			this.checkIfMetricListIsFilled();
		}
	}

	// ----- customLink (top/bottom) -----

	@tracked isMetricListFilled = false;

	get showCustomLink() {
		return (
			this.isConnectedSourceSelected &&
			this.resultMetrics.length > 0 &&
			(this.isQueryFilterSelected || this.isScriptsFilterSelected)
		);
	}

	get showCustomLinkAsBottomSticky() {
		return this.showCustomLink && this.isMetricListFilled;
	}

	get showCustomLinkAsListItem() {
		return this.showCustomLink && !this.isMetricListFilled;
	}

	get addCustomMetricText() {
		const metricText = this.isQueryFilterSelected ? 'Built' : 'Calculated';
		return `New ${metricText} metric...`;
	}

	@action
	checkIfMetricListIsFilled() {
		const listNode = this.listNode,
			contentNode = listNode?.parentNode as HTMLElement | null;

		if (listNode && contentNode) {
			const filterHeight =
				contentNode.querySelector<HTMLElement>('.filter-line')?.offsetHeight || 0;
			let ctHeight = contentNode.offsetHeight - filterHeight;

			const scrollHeight = listNode.scrollHeight;

			if (this.showCustomLinkAsBottomSticky) {
				ctHeight += 58; // hardcoded value for the link height
			}
			this.isMetricListFilled = scrollHeight > ctHeight;
		}
	}

	@action
	addCustomMetric() {
		if (this.isQueryFilterSelected) {
			this.createQuery();
		} else if (this.isScriptsFilterSelected) {
			this.createScript();
		}
	}

	// ----- tour -----

	checkTourStartConditions() {
		return isAlive(this) && this.args.isPanelOpen;
	}
	<template>
		<FilterLineComponent @model={{this.filterSort}} @theme="dark" />

		{{#if this.showCannotUseWarning}}

			<div class="section--blocked">

				<p>
					{{this.sourceName}}
					requires the
					{{this.limitationName}}
					feature which isn't available in your current plan.
				</p>

				<div class="upgrade-controls">
					{{#if this.accountService.isClient}}
						<p>
							Contact your organization administrator to upgrade your plan.
						</p>
					{{else}}
						{{#if this.canRequestTrial}}
							<CommonRequestTrialButtonComponent
								@feature={{this.limitationForSelectedSource}}
								@size="small"
								@trackingOrigin={{this.trackingOrigin}}
							/>
						{{else}}
							<CommonUpgradePlanButtonComponent
								@feature={{this.limitationForSelectedSource}}
								@size="small"
								@trackingOrigin={{this.trackingOrigin}}
							/>
						{{/if}}
					{{/if}}
				</div>

			</div>

		{{else if this.resultMetrics}}

			<ul
				class="metric-list"
				{{didInsert this.setupMetricList}}
				{{willDestroy this.teardownMetricList}}
				{{onWindowEvents this.checkIfMetricListIsFilled "resize" 200}}
				{{didUpdate this.checkToEndTour @isPanelOpen}}
			>
				<CommonListAutoLoadedComponent
					@allItems={{this.resultMetrics}}
					@onVisibleItemsChanged={{this.onListVisibleItemsChanged}}
					as |visibleMetrics|
				>
					{{#each visibleMetrics as |model|}}
						<DesignerLeftPanelMetricsMetricComponent
							@model={{model}}
							@typedFilter={{this.filterSort.typedFilter}}
							@onConnect={{this.connectSource}}
							@addToBoard={{this.addNewBlock}}
						/>
					{{/each}}
				</CommonListAutoLoadedComponent>

				{{#if this.showCustomLinkAsListItem}}
					<li class="metric-item custom-metric-link custom-metric-link--list-item">
						<a role="button" {{on "click" this.addCustomMetric}}>
							<CommonUiIconComponent @name="DatablockAdd" @size="sm" />
							{{this.addCustomMetricText}}
						</a>
					</li>
				{{/if}}
			</ul>

			{{#if this.showCustomLinkAsBottomSticky}}
				<div class="custom-metric-link custom-metric-link--sticky">
					<a role="button" {{on "click" this.addCustomMetric}}>
						<CommonUiIconComponent @name="DatablockAdd" @size="sm" />
						{{this.addCustomMetricText}}
					</a>
				</div>
			{{/if}}

		{{else}}

			<div class="section--noresults">

				<FilterNoResultsComponent @model={{this.filterSort}} />

				{{#if this.showNextStepsSection}}

					<div class="section__next-steps">

						<strong>
							{{#if this.selectedSourceHasNoMetrics}}Get started{{else}}Next steps...{{/if}}
						</strong>

						{{#if this.isEmptyVisualizationFlow}}

							{{! no metrics for this specific source at all }}

							<ol>
								<li
									class={{if
										this.isConnectedSourceSelected
										"connected"
										"not-connected"
									}}
								>
									{{#if this.isConnectedSourceSelected}}
										<CommonUiIconComponent
											@name="Check"
											@size="sm"
											@class="connected-icon"
										/>
										{{#if this.sourceType.isWithCustomToken}}
											Token created
										{{else}}
											Data source connected
										{{/if}}
									{{else}}
										<a
											role="button"
											class="button theme-green connect
												{{if this.sourceType.isTokenWrapper 'plus'}}"
											{{on
												"click"
												(fn
													this.connectSource
													(definite this.sourceType.key)
												)
											}}
										>
											{{#if this.sourceType.isTokenWrapper}}
												New token
											{{else}}
												<CommonUiIconComponent @name="Connect" @size="sm" />
												Connect
											{{/if}}
										</a>
									{{/if}}
								</li>
								{{#if this.sourceType.isZapier}}
									<li>
										Create your Zap by switching over to Zapier or use one of
										our Zap templates
									</li>
								{{else if this.sourceType.isMake}}
									<li>
										Create your Scenario by switching over to Make or use one of
										our Make templates
									</li>
								{{/if}}

								<li>
									Drag &amp; drop an empty visualization
								</li>

								{{#if this.sourceType.isWithCustomToken}}
									<li>
										Select the
										{{this.sourceType.name}}
										data source and pushed metric from the right settings panel
									</li>
								{{else}}
									<li>
										Select a data source and metric from the right settings
										panel.
										<br /><br />
										You can choose from available prebuilt metrics
										{{#if this.sourceType.isQBSupported}}
											or create a custom metric with Metric builder
										{{/if}}.
									</li>
								{{/if}}
							</ol>

						{{else}}

							{{#if this.showConnectInfo}}
								<SubmitButtonComponent
									@text="Connect"
									@theme="green"
									@extraClass="connect"
									@icon="Connect"
									@onTrigger={{fn
										this.connectSource
										(definite this.sourceType.key)
									}}
								/>
							{{else if this.showQbInfo}}
								<SubmitButtonComponent
									@text="New custom metric"
									@extraClass="connect"
									@icon="Plus"
									@onTrigger={{this.createQuery}}
								/>
							{{else if this.showScriptInfo}}
								<SubmitButtonComponent
									@text="New calculated metric"
									@extraClass="connect"
									@icon="Plus"
									@onTrigger={{this.createScript}}
								/>
							{{/if}}

							<ul>

								{{#if this.showConnectInfo}}
									{{#if this.sourceType.connectLinkWithFallback}}
										<li>
											<a
												href={{this.sourceType.connectLinkWithFallback}}
												target="_blank"
											>
												Learn more
											</a>
											on connecting
											{{this.sourceType.name}}
										</li>
									{{/if}}
								{{else}}
									{{#if this.sourceType.isGoogleSheets}}
										<li>
											<a role="button" {{on "click" this.openGSheetsVideo}}>
												Watch
											</a>
											Google Sheets video guide
										</li>
									{{/if}}
									{{#if this.showQbInfo}}
										<li>
											<a
												href={{this.sourceType.qbLinkWithFallback}}
												target="_blank"
											>
												Learn more
											</a>
											about using Metric builder to extract custom metrics
										</li>
									{{else if this.showScriptInfo}}
										<li>
											<a
												href="https://help.databox.com/overview-calculated-metrics"
												target="_blank"
											>
												Learn more
											</a>
											about using Data calculations
										</li>
									{{/if}}

									{{#if this.showMetricRequestInfo}}
										<li>
											<a role="button" {{on "click" this.openMetricRequest}}>
												Submit a metric request
											</a>
										</li>
									{{else}}
										<li>
											<a role="button" {{on "click" this.contactUs}}>
												Contact support
											</a>
											if you need additional help creating your desired metric
										</li>
									{{/if}}

								{{/if}}

							</ul>
						{{/if}}
					</div>
				{{/if}}
			</div>
		{{/if}}
	</template>
}
